import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

/**
 * One selectable accent. `shades` is a full 50..950 ramp because PrimeNG's Aura preset
 * reads every step (`--p-primary-50` … `--p-primary-950`) for hovers, focus rings,
 * disabled states and dark mode — setting only the 500 leaves half the components on
 * the old colour.
 */
export interface ThemeOption {
  key: string;
  name: string;
  /** The swatch shown in the picker (the 500 step). */
  swatch: string;
  shades: Record<string, string>;
}

const STORAGE_KEY = 'arogya.theme';

export const THEMES: ThemeOption[] = [
  {
    key: 'ocean',
    name: 'Ocean Blue',
    swatch: '#007cc2',
    shades: {
      50: '#e6f3fa', 100: '#cce7f4', 200: '#99cfe9', 300: '#66b7de', 400: '#339fd3',
      500: '#007cc2', 600: '#0070ae', 700: '#005c93', 800: '#004a76', 900: '#00395a', 950: '#00283f',
    },
  },
  {
    key: 'emerald',
    name: 'Emerald',
    swatch: '#0f9d76',
    shades: {
      50: '#e7f6f1', 100: '#c6ebe0', 200: '#8fd8c4', 300: '#57c4a7', 400: '#2bb08e',
      500: '#0f9d76', 600: '#0d8a68', 700: '#0a7255', 800: '#085a43', 900: '#064635', 950: '#043024',
    },
  },
  {
    key: 'indigo',
    name: 'Royal Indigo',
    swatch: '#4f46e5',
    shades: {
      50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc', 400: '#818cf8',
      500: '#4f46e5', 600: '#4338ca', 700: '#3730a3', 800: '#312e81', 900: '#282566', 950: '#1b1a45',
    },
  },
  {
    key: 'plum',
    name: 'Plum',
    swatch: '#9333ea',
    shades: {
      50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe', 400: '#c084fc',
      500: '#9333ea', 600: '#7e22ce', 700: '#6b21a8', 800: '#581c87', 900: '#46156b', 950: '#2f0e47',
    },
  },
  {
    key: 'sunset',
    name: 'Sunset',
    swatch: '#e0670b',
    shades: {
      50: '#fff4e8', 100: '#ffe6cc', 200: '#fecb99', 300: '#fbae66', 400: '#f59340',
      500: '#e0670b', 600: '#c65a09', 700: '#a34a07', 800: '#7f3a06', 900: '#642e05', 950: '#421e03',
    },
  },
  {
    key: 'graphite',
    name: 'Graphite',
    swatch: '#334155',
    shades: {
      50: '#f1f5f9', 100: '#e2e8f0', 200: '#cbd5e1', 300: '#94a3b8', 400: '#64748b',
      500: '#334155', 600: '#2b3849', 700: '#232e3c', 800: '#1b2430', 900: '#141b24', 950: '#0d1218',
    },
  },
  {
    key: 'teal',
    name: 'Teal',
    swatch: '#0d9488',
    shades: {
      50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4', 400: '#2dd4bf',
      500: '#0d9488', 600: '#0f766e', 700: '#115e59', 800: '#134e4a', 900: '#0f3d38', 950: '#042f2e',
    },
  },
  {
    key: 'sky',
    name: 'Sky',
    swatch: '#0284c7',
    shades: {
      50: '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd', 300: '#7dd3fc', 400: '#38bdf8',
      500: '#0284c7', 600: '#0369a1', 700: '#075985', 800: '#0c4a6e', 900: '#0a3d5c', 950: '#082f49',
    },
  },
  {
    key: 'violet',
    name: 'Violet',
    swatch: '#7c3aed',
    shades: {
      50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd', 400: '#a78bfa',
      500: '#7c3aed', 600: '#6d28d9', 700: '#5b21b6', 800: '#4c1d95', 900: '#3d1878', 950: '#2e1065',
    },
  },
  {
    key: 'rose',
    name: 'Rose',
    swatch: '#e11d48',
    shades: {
      50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af', 400: '#fb7185',
      500: '#e11d48', 600: '#be123c', 700: '#9f1239', 800: '#881337', 900: '#6b0f2c', 950: '#4c0519',
    },
  },
  {
    key: 'amber',
    name: 'Amber',
    swatch: '#d97706',
    shades: {
      50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d', 400: '#fbbf24',
      500: '#d97706', 600: '#b45309', 700: '#92400e', 800: '#78350f', 900: '#602c0c', 950: '#451a03',
    },
  },
  {
    key: 'forest',
    name: 'Forest',
    swatch: '#15803d',
    shades: {
      50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 300: '#86efac', 400: '#4ade80',
      500: '#15803d', 600: '#166534', 700: '#14532d', 800: '#103f22', 900: '#0d331c', 950: '#052e16',
    },
  },
  {
    key: 'wine',
    name: 'Wine',
    swatch: '#9f1239',
    shades: {
      50: '#fdf2f4', 100: '#fce7ea', 200: '#f9d0d8', 300: '#f2a8b8', 400: '#e57391',
      500: '#9f1239', 600: '#881337', 700: '#6f0f2c', 800: '#5a0c24', 900: '#4a0a1e', 950: '#2f0612',
    },
  },
  {
    key: 'midnight',
    name: 'Midnight',
    swatch: '#1e3a5f',
    shades: {
      50: '#f1f5f9', 100: '#e2e8f0', 200: '#cbd5e1', 300: '#94a3b8', 400: '#64748b',
      500: '#1e3a5f', 600: '#1a3252', 700: '#152941', 800: '#112034', 900: '#0d1927', 950: '#080f18',
    },
  },
];

/**
 * Live accent switching. Rewrites the brand custom properties AND PrimeNG's generated
 * `--p-primary-*` ramp on <html>, so plain CSS and every PrimeNG component recolour
 * together without a reload or a rebuilt theme.
 *
 * SSR-safe: the DOM writes and localStorage access are guarded, and the server renders
 * the default accent (which the browser then corrects on hydration).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);

  readonly themes = THEMES;
  readonly current = signal<ThemeOption>(THEMES[0]);

  /**
   * Call once at app start. Paints the locally-cached accent immediately, then reconciles
   * with the server.
   *
   * The two-step matters: the account is the source of truth (so the accent follows the
   * user to any browser), but waiting for a round trip before painting would flash the
   * default palette on every load. localStorage is therefore kept as a paint cache, not
   * as the record.
   */
  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const cached = this.readCache();
    const theme = THEMES.find((t) => t.key === cached);
    if (theme) this.apply(theme, false);
    this.syncFromServer();
  }

  /**
   * Pull the accent saved against the account and apply it if it differs from what is on
   * screen. Silent on failure — a preference is not worth an error toast, and the cached
   * accent is already a reasonable answer.
   */
  syncFromServer(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Only meaningful for a signed-in user; on the login page there is nothing to fetch.
    let token: string | null = null;
    try {
      token = localStorage.getItem('token');
    } catch {
      /* storage unavailable */
    }
    if (!token) return;

    this.http.get<any>(`${environment.apiUrl}/UserPreference`).subscribe({
      next: (res) => {
        const key = res?.data?.themeKey as string | undefined;
        if (!key) return; // never chose one — leave the default/cached accent alone
        if (key === this.current().key) return;
        const theme = THEMES.find((t) => t.key === key);
        if (theme) this.apply(theme, false), this.writeCache(theme.key);
      },
      error: () => {
        /* offline or endpoint unavailable — the cached accent stands */
      },
    });
  }

  /** Commit an accent: applies it, caches it locally, and saves it against the account. */
  set(key: string): void {
    const theme = THEMES.find((t) => t.key === key);
    if (!theme) return;
    this.apply(theme, true);
    this.persistToServer(key);
  }

  /** Write-behind: the UI has already updated, so a failed save must not block the user. */
  private persistToServer(key: string | null): void {
    if (!isPlatformBrowser(this.platformId)) return;
    let token: string | null = null;
    try {
      token = localStorage.getItem('token');
    } catch {
      /* storage unavailable */
    }
    if (!token) return;
    this.http
      .put(`${environment.apiUrl}/UserPreference/Theme`, { themeKey: key })
      .subscribe({ next: () => {}, error: () => {} });
  }

  private readCache(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeCache(key: string): void {
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      /* storage unavailable */
    }
  }

  /**
   * Apply an accent WITHOUT remembering it, so the picker can show a live preview that
   * `cancel()` can throw away. Nothing is written to storage until the user hits Apply.
   */
  preview(key: string): void {
    const theme = THEMES.find((t) => t.key === key);
    if (theme) this.apply(theme, false);
  }

  /** Re-apply whatever is actually saved — used when the picker is cancelled. */
  revert(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
    this.apply(THEMES.find((t) => t.key === saved) ?? THEMES[0], false);
  }

  /** Forget the saved accent and go back to the default, on this device AND on the account
   *  — clearing only the local cache would let the next sync restore it from the server. */
  reset(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* storage unavailable */
      }
    }
    this.apply(THEMES[0], false);
    this.persistToServer(null);
  }

  /** The accent currently persisted (null when none has been chosen). */
  savedKey(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private apply(theme: ThemeOption, persist: boolean): void {
    this.current.set(theme);
    if (!isPlatformBrowser(this.platformId)) return;

    const root = document.documentElement;
    for (const [step, hex] of Object.entries(theme.shades)) {
      // PrimeNG components (buttons, selects, focus rings, dialogs…)
      root.style.setProperty(`--p-primary-${step}`, hex);
    }
    // The app's own tokens, used by hand-written CSS across the pages.
    root.style.setProperty('--brand', theme.shades['500']);
    root.style.setProperty('--brand-dark', theme.shades['700']);
    root.style.setProperty('--brand-deep', theme.shades['800']);
    root.style.setProperty('--brand-50', theme.shades['50']);
    root.style.setProperty('--brand-100', theme.shades['100']);
    root.style.setProperty('--ring', `0 0 0 3px ${hexToRgba(theme.shades['500'], 0.18)}`);
    root.style.setProperty('--brand-soft', hexToRgba(theme.shades['500'], 0.08));
    root.style.setProperty('--brand-glow', hexToRgba(theme.shades['500'], 0.35));

    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, theme.key);
      } catch {
        // non-fatal: the accent still applies for this session
      }
    }
  }
}

/**
 * Read a brand token's CURRENT value from the DOM. Chart datasets, the Razorpay checkout
 * theme and anything else that needs a real colour string (not a CSS `var()`) must call
 * this at use time — a value captured at module load would freeze on the default accent.
 * Falls back to the Ocean default on the server, where there is no computed style.
 */
export function brandColor(token: '--brand' | '--brand-dark' | '--brand-deep' = '--brand'): string {
  if (typeof document === 'undefined') return token === '--brand' ? '#007cc2' : '#005c93';
  const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return v || (token === '--brand' ? '#007cc2' : '#005c93');
}

/** The live accent at an arbitrary alpha — for chart fills, which need a real colour
 *  string rather than a CSS `var()`. */
export function brandAlpha(alpha: number): string {
  return hexToRgba(brandColor('--brand'), alpha);
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(
    h.length === 3 ? h.split('').map((c) => c + c).join('') : h,
    16,
  );
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
